import React from "react";
import { Product, Recipe } from "../../types/mes";

interface ProductSpecCardProps {
  product: Product;
  onClick?: (product: Product) => void;
  compact?: boolean;
}

/**
 * ProductSpecCard - Displays product specifications in a card format
 * Used in the planning sidebar to show product details and allow quick-add to plan
 */
const ProductSpecCard: React.FC<ProductSpecCardProps> = ({
  product,
  onClick,
  compact = false
}) => {
  const activeRecipe = product.Recipe?.find(r => r.IsActive);

  const handleClick = () => {
    if (onClick) {
      onClick(product);
    }
  };

  const formatTime = (minutes?: number) => {
    if (!minutes) return "-";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTotalProcessTime = () => {
    const times = [
      product.MixingTimeMin,
      product.BulkFermentationMin,
      product.ProcessingTimeMin,
      product.FinalProofMin,
      product.BakingTime,
    ].filter(Boolean) as number[];

    if (times.length === 0) return null;
    return times.reduce((sum, t) => sum + t, 0);
  };

  const totalTime = getTotalProcessTime();

  if (compact) {
    return (
      <div
        className={`product-spec-card compact ${onClick ? "clickable" : ""}`}
        onClick={handleClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <div className="product-header">
          <span className="product-name">{product.ProductName}</span>
          {product.ProductCode && (
            <span className="product-code">{product.ProductCode}</span>
          )}
        </div>
        <div className="product-specs-row">
          <span className="spec-item">
            <span className="spec-value">{product.DefaultBatchMassKg || "-"}</span>
            <span className="spec-label">kg</span>
          </span>
          <span className="spec-item">
            <span className="spec-value">{product.PiecesPerBatch || "-"}</span>
            <span className="spec-label">kom</span>
          </span>
          <span className="spec-item">
            <span className="spec-value">{formatTime(product.MixingTimeMin)}</span>
            <span className="spec-label">mij</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`product-spec-card ${onClick ? "clickable" : ""}`}
      onClick={handleClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="product-header">
        <h4 className="product-name">{product.ProductName}</h4>
        {product.ProductCode && (
          <span className="product-code">{product.ProductCode}</span>
        )}
      </div>

      <div className="product-specs">
        <div className="spec-group">
          <div className="spec-row">
            <span className="spec-label">Masa zamjesa:</span>
            <span className="spec-value">{product.DefaultBatchMassKg || "-"} kg</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Komada/zamjes:</span>
            <span className="spec-value">{product.PiecesPerBatch || "-"}</span>
          </div>
          {product.UnitWeightG && (
            <div className="spec-row">
              <span className="spec-label">Tezina komada:</span>
              <span className="spec-value">{product.UnitWeightG} g</span>
            </div>
          )}
        </div>

        <div className="spec-group">
          <div className="spec-row">
            <span className="spec-label">Mijesanje:</span>
            <span className="spec-value">{formatTime(product.MixingTimeMin)}</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Fermentacija:</span>
            <span className="spec-value">{formatTime(product.BulkFermentationMin)}</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Obrada:</span>
            <span className="spec-value">{formatTime(product.ProcessingTimeMin)}</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Zavrsno dizanje:</span>
            <span className="spec-value">{formatTime(product.FinalProofMin)}</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Pecenje:</span>
            <span className="spec-value">{formatTime(product.BakingTime)}</span>
          </div>
          {totalTime && (
            <div className="spec-row total">
              <span className="spec-label">Ukupno:</span>
              <span className="spec-value">{formatTime(totalTime)}</span>
            </div>
          )}
        </div>

        {product.BakingTemp && (
          <div className="spec-group">
            <div className="spec-row">
              <span className="spec-label">Temp. pecenja:</span>
              <span className="spec-value">{product.BakingTemp}°C</span>
            </div>
          </div>
        )}

        {activeRecipe && (
          <div className="spec-group recipe-info">
            <div className="spec-row">
              <span className="spec-label">Aktivni recept:</span>
              <span className="spec-value recipe-code">
                {activeRecipe.RecipeCode || `R-${activeRecipe.ID}`}
              </span>
            </div>
          </div>
        )}
      </div>

      {onClick && (
        <div className="product-add-hint">
          + Dodaj u plan
        </div>
      )}
    </div>
  );
};

export default ProductSpecCard;
